import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useToast } from '../../components/ui/use-toast';
import { Users, Check, X, Loader2, Building, Mail, Lock, User } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const JoinTeamPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  
  // Registration form for new users
  const [isNewUser, setIsNewUser] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    password: '',
    password_confirm: ''
  });
  
  const token = searchParams.get('token');
  
  useEffect(() => {
    if (token) {
      validateInvitation();
    } else {
      setError('Token d\'invitation manquant');
      setLoading(false);
    }
  }, [token]);
  
  const validateInvitation = async () => {
    try {
      const response = await fetch(`${API_URL}/api/team/invitation/${token}`);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Invitation invalide');
      }
      
      setInvitation(data);
      setIsNewUser(!data.user_exists);
      
    } catch (err) {
      setError(err.message || 'Impossible de valider l\'invitation');
    } finally {
      setLoading(false);
    }
  };
  
  const handleAccept = async () => {
    // Validate form if new user
    if (isNewUser) {
      if (!formData.full_name.trim()) {
        toast({
          title: "Erreur",
          description: "Veuillez entrer votre nom complet",
          variant: "destructive"
        });
        return;
      }
      if (formData.password.length < 8) {
        toast({
          title: "Erreur",
          description: "Le mot de passe doit contenir au moins 8 caractères",
          variant: "destructive"
        });
        return;
      }
      if (formData.password !== formData.password_confirm) {
        toast({
          title: "Erreur",
          description: "Les mots de passe ne correspondent pas",
          variant: "destructive"
        });
        return;
      }
    }
    
    setSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/team/accept-invitation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token,
          ...(isNewUser && {
            full_name: formData.full_name,
            password: formData.password
          })
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Erreur lors de l\'acceptation');
      }
      
      setSuccess(true);
      
      toast({
        title: "Bienvenue dans l'équipe !",
        description: "Vous avez rejoint l'équipe avec succès"
      });
      
      // If we got a token, save it and redirect
      if (data.access_token) {
        localStorage.setItem('token', data.access_token);
        setTimeout(() => navigate('/dashboard/smartfacture'), 2000);
      } else {
        setTimeout(() => navigate('/login'), 2000);
      }
      
    } catch (err) {
      toast({
        title: "Erreur",
        description: err.message || "Impossible de rejoindre l'équipe",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleDecline = async () => {
    setSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/team/decline-invitation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token })
      });
      
      if (response.ok) {
        toast({
          title: "Invitation refusée",
          description: "Vous avez refusé l'invitation"
        });
        navigate('/');
      }
    } catch (err) {
      console.error('Error declining:', err);
    } finally {
      setSubmitting(false);
    }
  };
  
  const ROLES = {
    admin: { name: 'Administrateur', color: 'text-purple-600 bg-purple-100' },
    accountant: { name: 'Comptable', color: 'text-green-600 bg-green-100' },
    collaborator: { name: 'Collaborateur', color: 'text-blue-600 bg-blue-100' }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-white">Validation de l'invitation...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
        <Card className="w-full max-w-md bg-slate-800/50 border-slate-700">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mb-4">
              <X className="h-8 w-8 text-red-500" />
            </div>
            <CardTitle className="text-white">Invitation invalide</CardTitle>
            <CardDescription className="text-slate-400">{error}</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-slate-400 mb-6">
              Cette invitation a peut-être expiré ou a déjà été utilisée.
            </p>
            <Button asChild>
              <Link to="/">Retour à l'accueil</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
        <Card className="w-full max-w-md bg-slate-800/50 border-slate-700">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-4">
              <Check className="h-8 w-8 text-green-500" />
            </div>
            <CardTitle className="text-white">Bienvenue !</CardTitle>
            <CardDescription className="text-slate-400">
              Vous avez rejoint l'équipe avec succès
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-slate-400 mb-4">
              Redirection vers le tableau de bord...
            </p>
            <Loader2 className="h-6 w-6 animate-spin text-blue-500 mx-auto" />
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
      <Card className="w-full max-w-md bg-slate-800/50 border-slate-700">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mb-4">
            <Users className="h-8 w-8 text-blue-500" />
          </div>
          <CardTitle className="text-white">Invitation à rejoindre une équipe</CardTitle>
          <CardDescription className="text-slate-400">
            Vous avez été invité à rejoindre SmartFacture AI
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Invitation details */}
          <div className="bg-slate-700/30 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-3">
              <Building className="h-5 w-5 text-slate-400" />
              <div>
                <p className="text-xs text-slate-500">Organisation</p>
                <p className="text-white font-medium">{invitation?.organization || 'SmartFacture AI'}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-slate-400" />
              <div>
                <p className="text-xs text-slate-500">Email</p>
                <p className="text-white">{invitation?.email}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Users className="h-5 w-5 text-slate-400" />
              <div>
                <p className="text-xs text-slate-500">Rôle attribué</p>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${ROLES[invitation?.role]?.color || 'text-slate-400 bg-slate-600'}`}>
                  {ROLES[invitation?.role]?.name || invitation?.role}
                </span>
              </div>
            </div>
            
            {invitation?.invited_by && (
              <div className="flex items-center gap-3">
                <User className="h-5 w-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">Invité par</p>
                  <p className="text-white">{invitation.invited_by}</p>
                </div>
              </div>
            )}
          </div>
          
          {/* Registration form for new users */}
          {isNewUser && (
            <div className="space-y-4">
              <p className="text-sm text-slate-400">
                Créez votre compte pour rejoindre l'équipe :
              </p>
              
              <div className="space-y-2">
                <Label htmlFor="full_name" className="text-slate-300">Nom complet</Label>
                <Input
                  id="full_name"
                  type="text"
                  placeholder="Votre nom complet"
                  value={formData.full_name}
                  onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                  className="bg-slate-700/50 border-slate-600 text-white"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300">Mot de passe</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Minimum 8 caractères"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="bg-slate-700/50 border-slate-600 text-white"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password_confirm" className="text-slate-300">Confirmer le mot de passe</Label>
                <Input
                  id="password_confirm"
                  type="password"
                  placeholder="Confirmer le mot de passe"
                  value={formData.password_confirm}
                  onChange={(e) => setFormData({...formData, password_confirm: e.target.value})}
                  className="bg-slate-700/50 border-slate-600 text-white"
                />
              </div>
            </div>
          )}
          
          {/* Action buttons */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
              onClick={handleDecline}
              disabled={submitting}
            >
              <X className="h-4 w-4 mr-2" />
              Refuser
            </Button>
            <Button
              className="flex-1 bg-blue-600 hover:bg-blue-700"
              onClick={handleAccept}
              disabled={submitting}
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              Accepter
            </Button>
          </div>
          
          <p className="text-xs text-center text-slate-500">
            En acceptant, vous acceptez les{' '}
            <Link to="/cgu" className="text-blue-400 hover:underline">
              conditions d'utilisation
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default JoinTeamPage;
